import React from 'react';
import { ArrowRight, Brain } from 'lucide-react';
import T from '@/components/landing/copy';
import { tt, Section, Eyebrow } from '@/components/landing/atoms';

function OperationalIntelligence({ lang, onDemo }) {
  const isAr = lang === 'ar';

  // NOI sparkline points
  const noiPts = [62, 64, 66, 65, 68, 71, 73, 70, 74, 77, 79, 82, 84];
  const W = 380, H = 84, P = 6;
  const xN = (i) => P + (i * (W - 2 * P)) / (noiPts.length - 1);
  const yN = (v) => H - P - ((v - 55) * (H - 2 * P)) / 35;
  const noiPath = noiPts.map((v, i) => `${i ? 'L' : 'M'} ${xN(i)} ${yN(v)}`).join(' ');
  const noiArea = noiPath + ` L ${xN(noiPts.length - 1)} ${H - P} L ${xN(0)} ${H - P} Z`;

  // Occupancy heat strip — 24 cells (last 24 months)
  const heat = [78,82,84,86,87,89,90,88,87,86,88,90,91,92,92,93,93,94,94,93,93,94,94,93];
  const heatColor = (v) => {
    if (v >= 93) return 'rgba(0,168,107,0.95)';
    if (v >= 88) return 'rgba(0,168,107,0.65)';
    if (v >= 83) return 'rgba(31,209,138,0.40)';
    return 'rgba(255,181,71,0.55)';
  };

  // Stream items cycling
  const streamTones = {
    signal: 'var(--reva-signal-2)', mint: 'var(--reva-mint)',
    amber: 'var(--reva-amber)', coral: 'var(--reva-coral)',
  };

  const riskTone = (r) =>
    r === 'high' ? 'var(--reva-coral)' : r === 'med' ? 'var(--reva-amber)' : 'var(--reva-signal-2)';

  return (
    <Section className="py-24">
      {/* Headline */}
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <Eyebrow>{tt(T.ops.eyebrow, lang)}</Eyebrow>
          <h2 className="mt-5 reva-display text-3xl sm:text-5xl max-w-[22ch]">
            {tt(T.ops.titleA, lang)} <span className="reva-signal-text">{tt(T.ops.titleB, lang)}</span>
          </h2>
        </div>
        <p className="text-white/75 max-w-md text-[14px] leading-relaxed">{tt(T.ops.sub, lang)}</p>
      </div>

      {/* The cockpit */}
      <div className="mt-12 reva-glass-emerald p-3 sm:p-4 relative overflow-hidden">
        {/* Lattice + scan + grid texture */}
        <div className="absolute inset-0 reva-lattice opacity-60 pointer-events-none" />
        <div className="reva-scan" style={{ top: 0 }} />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[color:var(--reva-signal)] to-transparent opacity-70 pointer-events-none" />

        {/* Cockpit header bar */}
        <div className="relative flex items-center justify-between px-3 py-2.5 border-b border-white/[0.08] mb-3">
          <div className="flex items-center gap-2.5">
            <span className="w-2 h-2 rounded-full bg-[color:var(--reva-signal-2)] reva-pulse" />
            <span className="text-[10.5px] reva-mono tracking-[0.22em] text-white/80 uppercase">{tt(T.ops.panel.title, lang)}</span>
          </div>
          <div className="hidden sm:flex items-center gap-4 text-[10px] reva-mono text-white/55">
            <span>ORG · OPER-A · ARN</span>
            <span className="text-[color:var(--reva-mint)]">LATENCY 38ms</span>
            <span>NODES · 7/7</span>
          </div>
        </div>

        <div className="relative grid grid-cols-12 gap-3">
          {/* LEFT column · NOI + Occupancy */}
          <div className="col-span-12 lg:col-span-5 space-y-3">
            {/* NOI sparkline tile */}
            <div className="rounded-2xl border border-white/[0.08] bg-[#0F1316]/80 p-4 relative overflow-hidden">
              <div className="flex items-center justify-between">
                <div className="text-[10px] reva-mono tracking-[0.18em] text-white/55 uppercase">{tt(T.ops.panel.noi, lang)}</div>
                <div className="text-[10px] reva-mono text-[color:var(--reva-mint)]">+11.2% YoY</div>
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <div className="text-[32px] leading-none reva-mono text-white tabular-nums">34.2M</div>
                <div className="text-[12px] reva-mono text-white/55">SAR · TTM</div>
              </div>
              <svg viewBox={`0 0 ${W} ${H}`} className="w-full mt-2" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="rg-noi" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#1FD18A" stopOpacity="0.42" />
                    <stop offset="100%" stopColor="#1FD18A" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path d={noiArea} fill="url(#rg-noi)" />
                <path d={noiPath} fill="none" stroke="#1FD18A" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
                <circle cx={xN(noiPts.length - 1)} cy={yN(noiPts[noiPts.length - 1])} r="3" fill="#1FD18A" />
                <circle cx={xN(noiPts.length - 1)} cy={yN(noiPts[noiPts.length - 1])} r="6" fill="#1FD18A" opacity="0.22" className="reva-pulse" />
              </svg>
            </div>

            {/* Occupancy heat strip */}
            <div className="rounded-2xl border border-white/[0.08] bg-[#0F1316]/80 p-4">
              <div className="flex items-center justify-between">
                <div className="text-[10px] reva-mono tracking-[0.18em] text-white/55 uppercase">{tt(T.ops.panel.occ, lang)}</div>
                <div className="text-[10px] reva-mono text-[color:var(--reva-mint)]">94.0% · forecast</div>
              </div>
              <div className="mt-3 flex gap-[3px]">
                {heat.map((v, i) => (
                  <div key={i} className="flex-1 h-7 rounded-[3px]" style={{ background: heatColor(v) }} title={`${v}%`} />
                ))}
              </div>
              <div className="mt-2 flex justify-between text-[9.5px] reva-mono text-white/45 uppercase tracking-[0.14em]">
                <span>24M</span><span>12M</span><span>NOW</span>
              </div>
            </div>

            {/* Anomaly detection */}
            <div className="rounded-2xl border border-white/[0.08] bg-[#0F1316]/80 p-4">
              <div className="flex items-center justify-between">
                <div className="text-[10px] reva-mono tracking-[0.18em] text-white/55 uppercase">{tt(T.ops.panel.anomalies, lang)}</div>
                <span className="text-[10px] reva-mono text-[color:var(--reva-coral)]">3 flagged</span>
              </div>
              <div className="mt-3 space-y-2">
                {T.ops.anomalies.map((a, i) => (
                  <div key={i} className="flex items-center justify-between text-[12.5px]">
                    <div className="flex items-center gap-2 text-white/85">
                      <span className="relative inline-flex w-2 h-2">
                        <span className="absolute inset-0 rounded-full" style={{ background: riskTone(a.risk) }} />
                        {a.risk === 'high' && <span className="absolute inset-0 rounded-full reva-warn-pulse" />}
                      </span>
                      {tt(a.label, lang)}
                    </div>
                    <div className="text-[12px] reva-mono" style={{ color: riskTone(a.risk) }}>{a.delta}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CENTER column · Live signal stream */}
          <div className="col-span-12 lg:col-span-4">
            <div className="rounded-2xl border border-white/[0.08] bg-[#0F1316]/80 p-4 h-full relative overflow-hidden">
              <div className="flex items-center justify-between">
                <div className="text-[10px] reva-mono tracking-[0.18em] text-white/55 uppercase">{tt(T.ops.panel.streams, lang)}</div>
                <span className="inline-flex items-center gap-1.5 text-[10px] reva-mono text-[color:var(--reva-signal-2)]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--reva-signal-2)] reva-pulse" /> STREAMING
                </span>
              </div>
              {/* Vertical stream visual */}
              <div className="absolute top-12 bottom-3 start-3 w-px bg-gradient-to-b from-transparent via-[color:var(--reva-signal)]/50 to-transparent" />
              <div className="mt-3 space-y-2.5 relative">
                {T.ops.streamItems.map((s, i) => (
                  <div key={i} className="ms-5 relative group" style={{ animation: 'reva-rise 0.7s cubic-bezier(0.2,0.8,0.2,1) backwards', animationDelay: `${0.05 + i * 0.08}s` }}>
                    <span className="absolute -start-[1.35rem] top-1.5 w-2 h-2 rounded-full" style={{ background: streamTones[s.tone] || 'var(--reva-signal-2)', boxShadow: `0 0 10px ${streamTones[s.tone] || 'var(--reva-signal)'}` }} />
                    <div className="flex items-center gap-2">
                      <span className="text-[9.5px] tracking-[0.16em] reva-mono uppercase" style={{ color: streamTones[s.tone] || 'var(--reva-signal-2)' }}>{tt(s.tag, lang)}</span>
                      <span className="text-[10px] reva-mono text-white/40">· {(i * 7 + 12) % 60}s ago</span>
                    </div>
                    <div className="text-[13px] text-white/90 leading-snug">{tt(s.text, lang)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT column · AI recommendations */}
          <div className="col-span-12 lg:col-span-3">
            <div className="rounded-2xl border border-[color:var(--reva-signal)]/30 bg-gradient-to-br from-[#0F1316]/95 to-[rgba(0,168,107,0.05)] p-4 h-full relative overflow-hidden">
              <div className="flex items-center justify-between">
                <div className="text-[10px] reva-mono tracking-[0.18em] text-white/70 uppercase flex items-center gap-1.5">
                  <Brain className="h-3 w-3 text-[color:var(--reva-signal-2)]" strokeWidth={1.8} />
                  {tt(T.ops.panel.reco, lang)}
                </div>
                <span className="text-[10px] reva-mono text-[color:var(--reva-signal-2)]">{T.ops.recos.length} READY</span>
              </div>
              <div className="mt-3 space-y-2.5">
                {T.ops.recos.map((r, i) => (
                  <div key={i} className="rounded-xl border border-white/[0.08] bg-white/[0.025] p-3 hover:border-[color:var(--reva-signal)]/40 transition-colors">
                    <div className="text-[12.5px] text-white leading-snug">{tt(r.title, lang)}</div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-[10.5px] reva-mono text-[color:var(--reva-mint)]">{tt(r.impact, lang)}</span>
                      <span className="text-[10px] reva-mono text-white/55">{r.conf} conf</span>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={onDemo} className="mt-3 w-full justify-center reva-btn-emerald text-[12px] py-2" data-testid="reva-ops-cta">
                {isAr ? 'افتح مركز القيادة' : 'Open command center'} <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>

        {/* Cockpit footer */}
        <div className="relative mt-4 flex flex-wrap items-center justify-between gap-3 px-3 py-2.5 border-t border-white/[0.08] text-[10px] reva-mono text-white/55 uppercase tracking-[0.14em]">
          <div className="flex items-center gap-4">
            <span><span className="text-[color:var(--reva-signal-2)]">●</span> intelligence plane · active</span>
            <span className="hidden sm:inline"><span className="text-[color:var(--reva-mint)]">●</span> data plane · 2.4M ev/hr</span>
            <span className="hidden md:inline"><span className="text-[color:var(--reva-signal-2)]">●</span> operator plane · 38 agents</span>
          </div>
          <span>v2.6 · OPERATOR OS</span>
        </div>
      </div>
    </Section>
  );
}

export default OperationalIntelligence;
